import { useEffect, useState } from 'react';
import type { TranslationLanguage } from '@ast-data/shared';
import { authFetch } from '../../utils/authFetch';
import { defineStoredString } from '../../utils/storage';
import { useOnlineStatus } from '../sync/useOnlineStatus';
import { looksForeign, otherLanguage } from './translationState';

/** Why a translation did not come back — each has its own message. */
export type TranslationFailure =
    | 'offline'
    | 'notConfigured'
    | 'quotaExceeded'
    | 'rateLimited'
    | 'unavailable'
    // Not from the server: DeepL found the text already in the target language.
    | 'sameLanguage';

export class TranslationRequestError extends Error {
    readonly reason: TranslationFailure;

    constructor(reason: TranslationFailure) {
        super(reason);
        this.reason = reason;
        this.name = 'TranslationRequestError';
    }
}

const KNOWN_FAILURES: readonly TranslationFailure[] = [
    'notConfigured',
    'quotaExceeded',
    'rateLimited',
    'unavailable'
];

export interface MachineTranslation {
    text: string;
    /** What DeepL took the original for, in its own code ('de', 'en', 'fr' …). */
    sourceLang: string;
}

/**
 * The source language is named — the other of the two — because DeepL uses
 * the glossary only for a named one. Not for a text plainly in a third
 * language: DeepL detects that one, and names it in the answer.
 */
export async function requestTranslation({
    text,
    targetLang,
    projectId,
    context
}: {
    text: string;
    targetLang: TranslationLanguage;
    projectId: string;
    /** What the text is, as a heading over it — see translationContext. */
    context?: string;
}): Promise<MachineTranslation> {
    const sourceLang = looksForeign(text)
        ? undefined
        : otherLanguage(targetLang);
    let response: Response;
    try {
        response = await authFetch('/api/translate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                text,
                sourceLang,
                targetLang,
                projectId,
                context
            })
        });
    } catch {
        throw new TranslationRequestError('offline');
    }
    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
            error?: string;
        };
        const reason = KNOWN_FAILURES.find((failure) => failure === body.error);
        throw new TranslationRequestError(reason ?? 'unavailable');
    }
    return (await response.json()) as MachineTranslation;
}

/**
 * Whether the server has a DeepL key, as last heard. Kept on the device so a
 * field opened offline still knows to show its (disabled) translate button —
 * a button that appears only once online would look like a fault.
 */
const lastKnownStatus = defineStoredString<'yes' | 'no'>({
    key: 'translation_enabled',
    allow: ['yes', 'no']
});

/** The same for DeepL Voice, which only a paid key opens. */
const lastKnownVoice = defineStoredString<'yes' | 'no'>({
    key: 'translation_voice',
    allow: ['yes', 'no']
});

interface ServiceStatus {
    enabled: boolean;
    voice: boolean;
}

function lastKnown(): ServiceStatus {
    return {
        enabled: lastKnownStatus.read() === 'yes',
        voice: lastKnownVoice.read() === 'yes'
    };
}

let statusRequest: Promise<ServiceStatus> | null = null;

/** Asked once per page load; a later answer only matters after a reload. */
function fetchStatus(): Promise<ServiceStatus> {
    if (!statusRequest) {
        statusRequest = authFetch('/api/translate/status')
            .then((response) => (response.ok ? response.json() : null))
            .then((body: { enabled?: boolean; voice?: boolean } | null) => {
                if (body === null) {
                    // Not an answer — keep what was known.
                    statusRequest = null;
                    return lastKnown();
                }
                const status = {
                    enabled: body.enabled === true,
                    voice: body.voice === true
                };
                lastKnownStatus.write(status.enabled ? 'yes' : 'no');
                lastKnownVoice.write(status.voice ? 'yes' : 'no');
                return status;
            })
            .catch(() => {
                statusRequest = null;
                return lastKnown();
            });
    }
    return statusRequest;
}

export interface TranslationAvailability {
    /** The server can translate (or could, when last asked). */
    enabled: boolean;
    /** The server can open DeepL Voice sessions (or could, when last asked). */
    voice: boolean;
    online: boolean;
}

export function useTranslationAvailability(): TranslationAvailability {
    const online = useOnlineStatus();
    const [status, setStatus] = useState(lastKnown);
    useEffect(() => {
        let active = true;
        void fetchStatus().then((value) => {
            if (active) {
                setStatus(value);
            }
        });
        return () => {
            active = false;
        };
    }, [online]);
    return { ...status, online };
}
